import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getVersionList as getVersionListAPI } from "@/apis/instance";
import { getRegions, getAZ, getAddons, listUsersInTenant, validateInstanceDomainNameUtil } from "@/apis";
import { debounce } from "lodash";
import { notification } from "antd";
import WrappedSelect from "@/components/instance/config/WrappedSelect";
import { requiredRule } from "./useBasicComponent";
import { DomainNameInput } from "@/components/instance/config/DomainNameInput";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useTemplateComponent } from "./useTemplateComponent";

export const fetchRegions = async (setRegionEnum, t) => {
  try {
    const response = await getRegions();
    if (response.status === "OK") {
      const r = response.value.reduce((accumulator, item) => {
        accumulator[item] = { text: item };
        return accumulator;
      }, {});
      setRegionEnum(r);
    } else {
      setRegionEnum({});
    }
  } catch (error) {
    notification.error({
      description: t(
        "config.instance.error.region",
        "Fail to get region list from server.",
      ),
    });
  }
};

export const fetchAZ = async (region, setAZEnum, setZoneLoading, t, formRef) => {
  try {
    setZoneLoading(true);
    const response = await getAZ(region);
    if (response.status === "OK") {
      const r = response.value.reduce((accumulator, item) => {
        accumulator[item] = { text: item };
        return accumulator;
      }, {});
      setAZEnum(r);
      setZoneLoading(false);
      if(formRef && Object.keys(r).length > 0 && !formRef.current.getFieldValue("zone")) {
        formRef.current.setFieldsValue({
          zone: Object.keys(r)[0]
        });
      }
    }
  } catch (error) {
    setAZEnum({});
    notification.error({
      description: t(
        "config.instance.error.az",
        "Fail to get available zone from server.",
      ),
    });
  }
};

export const useGeneralConfig = ({ tenant, formRef, onValuesChangeCallback }) => {
  const { t } = useTranslation();

  const [versionsEnum, setVersionsEnum] = useState({});
  const [regionEnum, setRegionEnum] = useState({});
  const [region, setRegion] = useState("");
  const [azEnum, setAZEnum] = useState({});
  const [addonsEnum, setAddonsEnum] = useState({});
  const [usersEnum, setUsersEnum] = useState({});
  const [versionLoading, setVersionLoading] = useState(true);
  const [addonLoading, setAddonLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [regionLoading, setRegionLoading] = useState(true);
  const [zoneLoading, setZoneLoading] = useState(true);
  const [validateLoading, setValidateLoading] = useState(false);
  const { templateComponent } = useTemplateComponent({
    formRef,
    onValuesChangeCallback,
  });

  const platformsEnum = {
    "6.1": { text: "6.1" },
    "7.1": { text: "7.1" }
  };

  const {
    rootState: { eks },
  } = useResourceContext();

  const debouncedValidate = useCallback(
    debounce(async (form, dataIndex) => {
      console.log("validation :");
      await form.validateFields([dataIndex]);
    }, 1000),
    [],
  );

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setVersionLoading(true);
        const response = await getVersionListAPI();
        if (response.status === "OK") {
          const v = response.value.reduce((accumulator, item) => {
            var image_version = item.viaops_image_version;
            const majorVersion = image_version.split('.').slice(0, 2).join('.');
            if(item.sprint in accumulator) {
                var platforms = accumulator[item.sprint]['platform'];
                if(!platforms.includes(majorVersion)) {
                  accumulator[item.sprint]['platform'].push(majorVersion);
                  accumulator[item.sprint]['text'] = item.sprint + " ( "+accumulator[item.sprint]['platform'].join(" | ")+" )";
                }
            } else {
                accumulator[item.sprint] = { text: item.sprint + " ( "+majorVersion+" )", value: item.sprint, platform: [majorVersion] };
            }
            
            return accumulator;
          }, {});
          setVersionsEnum(v);
          setVersionLoading(false);
          if(Object.keys(v).length > 0) {
            formRef.current.setFieldsValue({
              version: Object.keys(v)[0]
            });
          }
        } else {
          setVersionsEnum({});
        }
      } catch (error) {
        notification.error({
          description: t(
            "config.instance.error.version",
            "Fail to get version list from server.",
          ),
        });
      }
    };

    fetchVersions();
  }, []);

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        setAddonLoading(true);
        const response = await getAddons();
        if (response.status === "OK") {
          const v = response.value.reduce((accumulator, item) => {
            accumulator[item.id] = { text: item.name };
            return accumulator;
          }, {});
          setAddonsEnum(v);
          setAddonLoading(false);
        } else {
          setAddonsEnum({});
        }
      } catch (error) {
        notification.error({
          description: t(
            "config.instance.error.version",
            "Fail to get addon list from server.",
          ),
        });
      }
    };

    fetchAddons();
  }, []);

  useEffect(() => {
    const fetchUsers = async (tenantName) => {
      try {
        setUserLoading(true);
        const response = await listUsersInTenant(tenantName);
        if (response.status === "OK") {
          const v = response.value.reduce((accumulator, item) => {
            accumulator[item.name] = { text: item.name };
            return accumulator;
          }, {});
          setUsersEnum(v);
          setUserLoading(false);
        } else {
          setUsersEnum({});
        }
      } catch (error) {
        notification.error({
          description: t(
            "config.instance.error.version",
            "Fail to get addon list from server.",
          ),
        });
      }
    };

    fetchUsers(tenant);
  }, []);

  useEffect(() => {
    const fetchContext = async () => {
      if (eks.isError) {
        setRegionLoading(true);
        notification.error({
          description: t(
            "config.instance.error.context",
            "Fail to get context from server. Please refresh browser later",
          ),
        });
        return;
      }
      if (eks.isLoading) {
        setRegionLoading(true);
        notification.error({
          description: t(
            "config.instance.error.contextLoading",
            "Loading context from server. Please re-launch instance later.",
          ),
        });
        return;
      }

      const { region: eksRegion } = eks.data;
      setRegionEnum({ [eksRegion]: { text: eksRegion } });
      setRegion(eksRegion);
      setRegionLoading(false);
      formRef.current.setFieldsValue({
        region: eksRegion,
      });
      await fetchAZ(eksRegion, setAZEnum, setZoneLoading, t, formRef);
    };

    fetchContext();
  }, []);

  const rawSchema = [
   {
      title: t("config.instance.form.name", "Name"),
      dataIndex: "name",
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      width: "400px", 
    },
    {
      title: t("config.instance.form.version", "Version"),
      dataIndex: "version",
      valueType: "select",
      valueEnum: versionsEnum,
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      width: "400px",
      fieldProps: {
        loading: versionLoading,
        // readOnly: true,
        // style: {
        //   pointerEvents: "none",
        //   backgroundColor: token.controlItemBgActiveDisabled,
        // },
        // disabled: true,
      },
      value: versionsEnum ? Object.keys(versionsEnum)[0] : ""
    },
    {
      title: t("config.instance.form.platform", "Platform"),
      dataIndex: "platform",
      valueType: "select",
      valueEnum: platformsEnum,
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      width: "400px",
      fieldProps: {
        // loading: versionLoading,
        // readOnly: true,
        // style: {
        //   pointerEvents: "none",
        //   backgroundColor: token.controlItemBgActiveDisabled,
        // },
        // disabled: true,
      },
      value: Object.keys(platformsEnum)[0],
      initialValue: Object.keys(platformsEnum)[0]
    },
    templateComponent(),
    {
      i18nTitle: "config.instance.form.region",
      title: t("config.instance.form.region", "Region"),
      dataIndex: "region",
      renderFormItem: (item, _, form) => {
        return (
          <WrappedSelect
            width="400px"
            options={regionEnum}
            value={region}
            loading={regionLoading}
            disabled={true}
            onChange={async (newVariables) => {
              form.setFieldsValue({
                [item.dataIndex]: newVariables,
              });
              await fetchAZ(newVariables, setAZEnum, t, formRef);
            }}
          />
        );
      },
      width: "400px",
    },

    {
      title: t("config.instance.form.az", "AZ"),
      dataIndex: "zone",
      valueType: "select",
      valueEnum: azEnum,
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      width: "400px",
      fieldProps: {
        mode: 'multiple',
        loading: zoneLoading,
      },
    },
    {
      title: t("config.instance.form.domain", "Domain"),
      dataIndex: "domain",
      renderFormItem: (item, _, form) => {
        return (
          <DomainNameInput
            placeholder={t(
              "config.instance.placeholder.domainName",
              "Use the instance name as the domain name if unspecified.",
            )}
            value=""
            loading={validateLoading}
            onChange={async (newVariables) => {
              form.setFieldsValue({
                [item.dataIndex]: newVariables,
              });
              // await form.validateFields([item.dataIndex]);
              debouncedValidate(form, item.dataIndex);
            }}
            onCheckDuplicated={async () => {
              setValidateLoading(true);
              try {
                await form.validateFields([item.dataIndex]);
              } finally {
                setValidateLoading(false);
              }

              // form.setFields([
              //   {
              //     name: item.dataIndex,
              //     // errors: ["This domain name is already taken."],
              //     errors: [],
              //   },
              // ]);
            }}
          />
        );
      },
      formItemProps: {
        rules: [
          ({ getFieldValue }) => ({
            async validator(_, value) {
              const domain =
                value != undefined && value != ""
                  ? value
                  : getFieldValue("name");
              const data = await validateInstanceDomainNameUtil(domain);
              if (data.status === "OK") {
                return Promise.resolve();
              }
              return value != undefined && value != ""
                ? Promise.reject(
                    new Error(
                      t("config.instance.error.domainName", { domain }),
                    ),
                  )
                : Promise.reject(
                    new Error(
                      t("config.instance.error.instanceASDomainName", {
                        domain,
                      }),
                    ),
                  );
            },
          }),
        ],
      },
    },
    {
      i18nTitle: "config.instance.form.users",
      title: "Add Tenant Users",
      dataIndex: "users",
      valueType: "select",
      valueEnum: usersEnum,
      width: "400px",
      fieldProps: {
        mode: 'multiple',
        loading: userLoading,
      },
    },
    {
      i18nTitle: "config.instance.form.additionAddons",
      title: "Install additional Add-ons",
      dataIndex: "additionAddons",
      valueType: "select",
      valueEnum: addonsEnum,
      width: "400px",
      fieldProps: {
        mode: 'multiple',
        loading: addonLoading,
      },
    },
    {
      i18nTitle: "config.instance.form.enableDruid",
      title: "Enable Druid",
      dataIndex: "enableDruid",
      valueType: "switch",
      initialValue: false,
    },
    {
      i18nTitle: "config.instance.form.enableGitea",
      title: "Enable Gitea",
      dataIndex: "enableGitea",
      valueType: "switch",
      initialValue: false,
    },
    {
      i18nTitle: "config.instance.form.enableNeo4j",
      title: "Enable Neo4j",
      dataIndex: "enableNeo4j",
      valueType: "switch",
      initialValue: false,
    },
    {
      i18nTitle: "config.instance.form.enableAPIGatewayMSK",
      title: "Enable APIGatewayMSK",
      dataIndex: "enableAPIGatewayMSK",
      valueType: "switch",
      initialValue: false,
    },
    {
      i18nTitle: "config.instance.form.enableSelfMonitoring",
      title: "Enable Self Monitoring",
      dataIndex: "enableSelfMonitoring",
      valueType: "switch",
      initialValue: true,
    },
    {
      i18nTitle: "config.instance.form.enableDiagnosticsUpload",
      title: "Enable Diagnostics Upload",
      dataIndex: "enableDiagnosticsUpload",
      valueType: "switch",
      initialValue: true,
    },
    {
      i18nTitle: "config.instance.form.startAllModules",
      title: "Start all Modules",
      dataIndex: "startAllModules",
      valueType: "switch",
      initialValue: true,
    },
    {
      i18nTitle: "config.instance.form.enableBiggerPoolSize",
      title: "Use Bigger Pool Size",
      dataIndex: "enableBiggerPoolSize",
      valueType: "switch",
      initialValue: true,
    },
  ];

  return rawSchema;
};
